import { Mql } from '@deqo/mql-js';
import { Arr } from '../../../CodesForFoundation';
import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { stringStep } from '../../../concepts/commands/CommandWizard';
import { Component, ComponentSelector, ObjectManager } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { connectorCreateBetween } from '../../packages/connector/ConnectorCommands';
import { LensReflector } from '../../packages/lens/LensReflector';
import { MqlUtils } from '../../packages/lens/MqlUtils';
import { Camera } from '../Camera';
import { EditorSession } from '../EditorSession';
import { PointerInfo } from '../PointerInfo';
import { Selection, SelectionSet } from '../Selection';
import { Physical, PotentialInteraction, RelativeTo } from '../entities/Physical';
import { Physicals } from '../entities/Physicals';
import { Repeatable } from '../entities/Repeatable';
import { ContentInjector } from '../injection/ContentInjector';
import { DeqoData } from '../injection/DeqoData';
import { PrevToolInfo } from '../tooling/ToolInfo';
import { ToolManager } from '../tooling/ToolManager';
import { SpatialIndex } from '../utilities/SpatialIndex';
import { resolvePhysicals, resolveSubjects } from './CommandUtil';
import { beginMutating } from './ManipulationCommands';

export const deleteCascade = defineCommand(
    'deleteCascade',
    null,
    (r, [targetIds]: [string[]]) => {

        const objects = r.call(ObjectManager);
        const targets = targetIds.map(x => objects.getMaybe(x)).filter(x => !!x);

        // To do a delete of a full tree, we need to unmount/disconnect everything before we delete.  To do this lets build a list in top to
        // bottom order then process the list in reverse.
        const allTargets = Physicals.top(targets).flatMap(x => x.flatten(true));

        objects.update(u => {
            Arr.rfor(allTargets, t => {
                if (t.parent) t.removeSelf();
                if (t.alive) u.delete(t);
            });
        });
    },
);

export const deleteSelection = defineCommand(
    'deleteSelection',
    'Selection: Delete',
    (r, [soft]: [boolean?]) => {
        soft = !!soft;

        // Resolve dependencies
        const session = r.call(EditorSession);
        const objects = r.call(ObjectManager);

        // Wipe selection first but keep previous value so we can do the delete
        const targets = session.get(Selection);
        const top = Physicals.top(targets);

        // Don't delete if anything is locked...
        if (soft && top.some(x => x.locked)) return;

        session.update(Selection, SelectionSet.empty);
        objects.update(u => {
            u.deleteAll(top, 'cascade');
        });
    },
);

export const clearSelection = defineCommand(
    'clearSelection',
    'Selection: Clear',
    (r) => {
        r.call(EditorSession).update(Selection, SelectionSet.empty);
    },
);

export const cloneSelection = defineCommand(
    'cloneSelection',
    'Selection: Clone',
    (r, [direction]: [('up' | 'down' | 'left' | 'right')?]) => {
        // Resolve dependencies
        const injector = r.call(ContentInjector);
        const session = r.call(EditorSession);
        // const objects = r.call(ObjectManager);

        // When cloning, nested elements will be taken care of by the tuplization process so we only need to deal with the top elements.  We
        // also want to compute the tuples for these top elements relative to the model, rather than the default of the parent, this is
        // because the injector is general purpose and used for scenarios where the non-model root is not always the same, so it assumes
        // tuples that are relative to the model root.

        // Collect top elements and wipe selection
        const subjects = Physicals.topToBottom(Physicals.top(session.update(Selection, SelectionSet.empty)));

        // Generate tuples relative to the model root
        const tuples = DeqoData.wrap(subjects.map(x => x.toTuple(RelativeTo.Model)));

        // If direction specified, compute position
        let position = null as Point;
        if (direction) {
            const bounds = Physicals.bounds(subjects);
            const vector = Point[direction].multiply(bounds.size());
            position = bounds.center().add(vector);
        }

        // Inject, oo-er
        injector.injectNative(tuples, { position, place: true, focus: 'instant' });
    },
);

export const cloneThenConnect = defineCommand(
    'cloneThenConnect',
    'Selection: Clone Then Connect',
    (cc, [direction]: [('up' | 'down' | 'left' | 'right')?]) => {
        // Resolve dependencies
        const injector = cc.call(ContentInjector);
        const session = cc.call(EditorSession);
        const objects = cc.call(ObjectManager);

        const subject = session.get(Selection).firstAndOnly();
        if (!subject) return;

        // Generate tuple relative to the model root
        const tuples = DeqoData.wrap([subject.toTuple(RelativeTo.Model)]);

        objects.update(u => {

            // If direction specified, compute position
            let position = null as Point;
            if (direction) {
                const bounds = subject.bounds(RelativeTo.Model);
                const vector = Point[direction].multiply(bounds.size());
                position = bounds.center().add(vector).add(vector.normalize().multiply(200));
            }

            // Inject, oo-er
            injector.injectNative(tuples, { position, place: true, focus: 'instant' });

            const clone = session.get(Selection).firstAndOnly();
            cc.dispatch(connectorCreateBetween, [subject.id, clone.id]);
            cc.dispatch(beginMutating);

        });
    },
);

export const select = defineCommand(
    'select',
    'Select: Targets...',
    (cc, [targets, shouldAppend]: [any, boolean?]) => {
        // Resolve dependencies
        const elmts = resolvePhysicals(cc, targets);
        const session = cc.call(EditorSession);
        session.apply(Selection, s => shouldAppend
            ? s.addMany(elmts)
            : SelectionSet.create(elmts)
        );
    },
    [
        stringStep('targets', 'What do you want to select?'),
    ]
);

export const collectAtCursor = defineCommand(
    'collectAtCursor',
    'Collect At Cursor',
    (r, [mode, force]: [('next' | 'all')?, boolean?]) => {
        mode = mode ?? 'next';

        const camera = r.call(Camera);
        const session = r.call(EditorSession);

        const pointer = session.get(PointerInfo);
        const selection = session.get(Selection);

        const mpt = camera.toModel(session.get(PointerInfo).position);
        const unwrap = ([hit]: any[]) => !!hit;
        const candidates = pointer.targets.filter(x =>
            (x instanceof Component) &&
            !selection.has(x) &&
            (!x.locked || force) &&
            !!unwrap(x.testInteraction(PotentialInteraction('select', mpt)))
        );

        if (candidates.length) {
            if (mode === 'next') {
                candidates.splice(1, candidates.length - 1);
            }
            session.apply(Selection, s => s.addMany(candidates as Physical[]));
            return true;
        }

        return false;
    },
);

export const selectSelector = defineCommand(
    'selectSelector',
    'Select: By Selector...',
    (r, [selector]: [string]) => {
        // Resolve dependencies
        const session = r.call(EditorSession);
        const objects = r.call(ObjectManager);
        const compiledSelector = ComponentSelector.compile(selector);
        const results = objects.query(compiledSelector) as Physical[];
        session.update(Selection, SelectionSet.create(results));
    },
    [
        stringStep('query', 'Enter the query:')
    ]
);

export const selectQuery = defineCommand(
    'selectQuery',
    'Select: By Query...',
    (r, [query]: [string]) => {
        // Resolve dependencies
        const reflector = r.call(LensReflector);
        const session = r.call(EditorSession);
        const objects = r.call(ObjectManager);
        const mqlUtils = r.call(MqlUtils);
        const scope = mqlUtils.createScope();
        const compiled = Mql.compile(query);
        const reflecting = reflector.createProxies(objects.findAll(Physical, x => x.mounted()));
        const results = reflecting.filter(x => compiled.test(scope, x[1])).map(x => x[0]);
        session.update(Selection, SelectionSet.create(results));
    },
    [
        stringStep('query', 'Enter the query:')
    ]
);

export const selectName = defineCommand(
    'selectName',
    'Select: By Name...',
    (r, [name]: [string]) => {
        // Resolve dependencies
        const session = r.call(EditorSession);
        const objects = r.call(ObjectManager);

        const root = objects.getRoot();
        const results = [] as Physical[];

        root.visitAll(x => {
            const p = x.as(Physical);
            if (p && p.name === name) results.push(p);
        });

        session.update(Selection, SelectionSet.create(results));
    },
    [
        stringStep('name', 'Enter the name:')
    ]
);

export const selectAll = defineCommand(
    'selectAll',
    'Select: All',
    r => {
        // Resolve dependencies
        const camera = r.call(Camera);
        const session = r.call(EditorSession);
        const spatial = r.call(SpatialIndex);

        const view = camera.getModelFrame();
        const candidates = Array.from(spatial.lookup(view))
            .filter(x =>
                x.bounds(RelativeTo.Model).intersects(view) &&
                !x.locked &&
                !x.bounded()
            );

        session.update(Selection, SelectionSet.create(candidates));
    },
);

export const selectOthers = defineCommand(
    'selectOthers',
    null,
    (r, [targetIds]: [string[]?]) => {
        // Resolve dependencies
        const camera = r.call(Camera);
        const session = r.call(EditorSession);
        const spatial = r.call(SpatialIndex);
        const types = new Set(resolveSubjects(r, targetIds).map(x => x.type));

        const view = camera.getModelFrame();
        const candidates = Array.from(spatial.lookup(view))
            .filter(x =>
                !x.locked &&
                types.has(x.type) &&
                x.bounds(RelativeTo.Model).intersects(view)
            );

        session.update(Selection, SelectionSet.create(candidates));
    },
);

export const repeat = defineCommand(
    'repeat',
    null,
    cc => {
        const session = cc.resolve(EditorSession);
        const selection = session.get(Selection);
        const tools = cc.resolve(ToolManager);

        if (selection.size) {
            const [first] = selection.polarize(Repeatable);
            if (!first) return;

            const info = first.queryRepeat();
            if (!info) return;

            tools.equip(info.toolId, info.toolSettings);
        }
        else {
            const info = session.get(PrevToolInfo);
            if (info?.id) {
                tools.equip(info.id);
            }
        }
    },
);

export const inSelectionCommands = () => inCommands(
    deleteSelection,
    clearSelection,
    cloneSelection,
    select,
    selectSelector,
    selectQuery,
    selectName,
    selectAll,
    selectOthers,
);
